/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { getAuth } from '@/auth/authRef'
import { openSignInModal } from '@/signInDialog/openSignInModal'
import { AuthStatus } from '@amzn/bf-playback'

export const SIGN_IN_MODAL_ACTION_DETAIL = 'OpenSignInModal'

export interface SignInMetrics {
    readonly actionDetail?: typeof SIGN_IN_MODAL_ACTION_DETAIL
}

export interface SignInGate {
    readonly isSignInRequired: boolean
    /** Spread into the action's uiClick after its own fields: a gated click keeps its actionType. */
    readonly signInMetrics: SignInMetrics
}

const NOT_GATED: SignInGate = { isSignInRequired: false, signInMetrics: {} }
const GATED: SignInGate = {
    isSignInRequired: true,
    signInMetrics: { actionDetail: SIGN_IN_MODAL_ACTION_DETAIL },
}

/**
 * GH `showSignInModalIfAnonymousV2`: opens the sign-in dialog when the session is anonymous and
 * returns the click fields that tag the gated press, so the action still reports its own uiClick.
 * Gates on SIGNED_OUT only: a PENDING cold start isn't yet anonymous, and a signed-in FREE
 * customer (a tier gate, not an auth gate) never trips this.
 */
export function showSignInModalIfAnonymousV2(refMarker?: string): SignInGate {
    if (getAuth().status.value !== AuthStatus.SIGNED_OUT) return NOT_GATED
    openSignInModal(refMarker)
    return GATED
}

/** The gate for a press that sends no uiClick of its own: true when the dialog opened. */
export function promptSignInIfAnonymous(refMarker?: string): boolean {
    return showSignInModalIfAnonymousV2(refMarker).isSignInRequired
}

/** A Library tab press: signed out, GH opens the sign-in dialog and stays on the page. */
export function holdLibraryForSignIn(event: Event): void {
    if (promptSignInIfAnonymous()) event.preventDefault()
}
